import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { localApiCatcher } from 'global/helperFunctions/localApiCatcher';
import ApiService, { errorResponse } from 'global/utils/ApiService';
import { ApiResponseError, assertApiSuccess } from './apiResponse';

type ApiMethod = 'post' | 'put' | 'delete' | 'patch';

interface UseApiMutationOptions<TData = any, TVariables = any> extends Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'> {
  /** The HTTP method to use. Default: 'post' */
  method?: ApiMethod;
  /** Query keys to invalidate after a successful mutation */
  invalidateKeys?: readonly (readonly unknown[])[];
  /** Show success notification. Pass a string for custom message. Default: false */
  successMessage?: boolean | string;
  /** Show error notification from API response. Default: true */
  showError?: boolean;
  /** Whether to show the network-error catcher. Default: true */
  catchNetworkError?: boolean;
  /** Reject API responses with isSuccess: false. Defaults to false for backwards compatibility. */
  rejectOnApiError?: boolean;
}

/**
 * Generic React Query mutation hook for create/update/delete operations.
 *
 * Wraps useMutation to work with the existing ApiService singleton,
 * with automatic query invalidation and optional success/error notifications.
 *
 * @example
 * // Create
 * const createClient = useApiMutation(endpointUrls.clientCreate, {
 *   invalidateKeys: [queryKeys.clients],
 *   successMessage: 'مشتری با موفقیت ایجاد شد',
 * });
 * createClient.mutate({ fullName: '...', mobile: '...' });
 *
 * @example
 * // Delete with dynamic URL
 * const deleteClient = useApiMutation({
 *   invalidateKeys: [queryKeys.clients],
 *   successMessage: 'مشتری با موفقیت حذف شد',
 * });
 * deleteClient.mutate({ _url: endpointUrls.clientDelete(id) });
 */
export function useApiMutation<TData = any, TVariables = Record<string, any>>(
  urlOrOptions: string | UseApiMutationOptions<TData, TVariables>,
  extraOptions?: UseApiMutationOptions<TData, TVariables>
) {
  const url = typeof urlOrOptions === 'string' ? urlOrOptions : undefined;
  const opts: UseApiMutationOptions<TData, TVariables> = typeof urlOrOptions === 'string' ? extraOptions || {} : urlOrOptions;

  const {
    method = 'post',
    invalidateKeys,
    successMessage = false,
    showError = true,
    catchNetworkError = true,
    rejectOnApiError = false,
    onSuccess,
    onError,
    ...mutationOptions
  } = opts;

  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVariables & { _url?: string; _method?: ApiMethod; _noNotify?: boolean }>({
    mutationFn: async (variables) => {
      const { _url, _method, _noNotify, ...payload } = variables as any;
      const requestUrl = _url || url;
      const requestMethod = _method || method;

      if (!requestUrl) {
        throw new Error('No URL provided for mutation. Pass a url or include _url in the variables.');
      }

      let response: any;
      switch (requestMethod) {
        case 'put':
          response = await ApiService.put(requestUrl, payload);
          break;
        case 'delete':
          response = await ApiService.delete(requestUrl);
          break;
        case 'patch':
          response = await ApiService.patch(requestUrl, payload);
          break;
        case 'post':
        default:
          response = await ApiService.post(requestUrl, payload);
          break;
      }
      return (rejectOnApiError ? assertApiSuccess(response) : response) as TData;
    },

    onSuccess: (data, variables, _onMutateResult, context) => {
      const isSuccess = (data as any)?.isSuccess !== false;
      if (isSuccess && successMessage) {
        const msg = typeof successMessage === 'string' ? successMessage : 'عملیات با موفقیت انجام شد.';
        message.success(msg);
      }

      if (isSuccess && invalidateKeys) {
        invalidateKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key as any });
        });
      }

      onSuccess?.(data, variables, _onMutateResult, context);
    },

    onError: (error: any, variables, _onMutateResult, context) => {
      if (error instanceof ApiResponseError) {
        if (showError) message.warning(error.message);
      } else if (catchNetworkError) {
        localApiCatcher(errorResponse);
      }
      // Also call the user's onError
      onError?.(error, variables, _onMutateResult, context);
    },

    ...mutationOptions,
  });
}

export default useApiMutation;
